import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, of, switchMap, take } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { superAdminNavigation, storeAdminNavigation } from 'app/mock-api/common/navigation/data';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _userService = inject(UserService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data based on user role
     */
    get(): Observable<Navigation> {
        return this._userService.user$.pipe(
            take(1), // Take only the first emission and complete
            switchMap(user => {
                // Determine which navigation to use based on user role
                const isSuperAdmin = user?.is_super_admin || false;
                const navItems = isSuperAdmin
                    ? cloneDeep(superAdminNavigation)
                    : cloneDeep(storeAdminNavigation);

                const navigation: Navigation = {
                    compact: navItems,
                    default: navItems,
                    futuristic: navItems,
                    horizontal: navItems,
                };

                this._navigation.next(navigation);
                return of(navigation);
            })
        );
    }

    /**
     * Refresh navigation based on current user
     */
    refresh(): void {
        this._userService.user$.subscribe(user => {
            const isSuperAdmin = user?.is_super_admin || false;
            const navItems = isSuperAdmin
                ? cloneDeep(superAdminNavigation)
                : cloneDeep(storeAdminNavigation);

            const navigation: Navigation = {
                compact: navItems,
                default: navItems,
                futuristic: navItems,
                horizontal: navItems,
            };

            this._navigation.next(navigation);
        }).unsubscribe();
    }
}
